import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { adminListGifts, adminAddGift, adminDeleteGift } from "../lib/api";
import { NewHomeProgress } from "../components/NewHomeProgress";

const input = "rounded-md border border-black/15 bg-white px-3 py-2 text-sm";

export function NewHomeAdmin() {
  const qc = useQueryClient();
  const { data: gifts = [] } = useQuery({ queryKey: ["admin-gifts"], queryFn: adminListGifts });
  const [form, setForm] = useState({ donor_name: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-gifts"] });
    qc.invalidateQueries({ queryKey: ["new-home-progress"] });
  };

  const add = async () => {
    if (!form.donor_name.trim() || !(Number(form.amount) > 0)) { toast.error("Enter a name and an amount."); return; }
    setSaving(true);
    try {
      await adminAddGift({ ...form, amount: Number(form.amount) });
      toast.success("Gift added");
      setForm({ donor_name: "", amount: "", note: "" });
      refresh();
    } catch { toast.error("Could not add gift"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await adminDeleteGift(id); toast.success("Gift removed"); refresh(); } catch { toast.error("Could not remove gift"); }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="admin-new-home">
      <div className="rounded-xl bg-white border border-black/10 p-6">
        <p className="text-sm text-black/60 mb-4">Online sponsorships count automatically. Add checks, cash and other offline gifts below.</p>
        <NewHomeProgress dark={false} />
      </div>
      <div className="grid gap-2 md:grid-cols-[1fr_140px_1fr_auto]">
        <input className={input} placeholder="Donor name" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} data-testid="gift-name" />
        <input className={input} type="number" min="1" placeholder="Amount $" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="gift-amount" />
        <input className={input} placeholder="Note (e.g. check #1042)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="gift-note" />
        <button onClick={add} disabled={saving} className="btn-primary text-sm !py-2 !px-4 inline-flex items-center gap-2" data-testid="gift-add">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
        </button>
      </div>
      <div className="divide-y divide-black/10 rounded-xl border border-black/10 bg-white" data-testid="gift-list">
        {gifts.length === 0 && <p className="p-4 text-sm text-black/50">No offline gifts yet.</p>}
        {gifts.map((g) => (
          <div key={g.id} className="flex items-center justify-between p-4 text-sm" data-testid={`gift-row-${g.id}`}>
            <div>
              <p className="font-semibold">{g.donor_name} · ${Number(g.amount).toLocaleString()}</p>
              <p className="text-black/55">{new Date(g.created_at).toLocaleDateString()}{g.note ? ` · ${g.note}` : ""}</p>
            </div>
            <button onClick={() => remove(g.id)} className="text-black/50 hover:text-red-600" data-testid={`gift-del-${g.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
